import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  ScrollView,
  Dimensions,
  Platform,
  Animated,
} from 'react-native';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { theme as baseTheme } from '../styles/theme';
import { useAuth } from '../hooks/useAuth';
import { useSheets } from '../hooks/useSheets';
import { useVoiceCommands, isVoiceSupported } from '../hooks/useVoiceCommands';

const { width: SCREEN_WIDTH } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';

// ─── Audio ───────────────────────────────────────────────────────────────────
let audioContext = null;
const getAudioContext = () => {
  if (isWeb && !audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  return audioContext;
};

const playMetronomeClick = (volume = 0.7, isAccent = false) => {
  if (!isWeb) return;
  try {
    const ctx = getAudioContext();
    if (!ctx || ctx.state === 'suspended') { ctx?.resume(); return; }
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.frequency.value = isAccent ? 1000 : 700;
    osc.type = 'sine';
    const now = ctx.currentTime;
    gain.gain.setValueAtTime(volume * (isAccent ? 1 : 0.6), now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
    osc.start(now); osc.stop(now + 0.08);
  } catch (e) { /* silently ignore */ }
};

const getDarkMode = () => {
  if (isWeb && typeof localStorage !== 'undefined') {
    return localStorage.getItem('sheetshed_darkmode') === 'true';
  }
  return false;
};

// ─── SVG Icons ───────────────────────────────────────────────────────────────
const ChevronLeftIcon = ({ color = '#000', size = 18 }) => {
  if (!isWeb) return <Text style={{ color, fontSize: size }}>{'<'}</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15,18 9,12 15,6" />
    </svg>
  );
};

const ChevronRightIcon = ({ color = '#000', size = 18 }) => {
  if (!isWeb) return <Text style={{ color, fontSize: size }}>{'>'}</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9,18 15,12 9,6" />
    </svg>
  );
};

const PlayIcon = ({ color = '#000', size = 20 }) => {
  if (!isWeb) return <Text style={{ color, fontSize: size }}>{'▶'}</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
      <polygon points="5,3 19,12 5,21" />
    </svg>
  );
};

const StopIcon = ({ color = '#000', size = 20 }) => {
  if (!isWeb) return <Text style={{ color, fontSize: size }}>{'■'}</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
      <rect x="4" y="4" width="16" height="16" rx="2" />
    </svg>
  );
};

const SpeakerOnIcon = ({ color = '#000', size = 18 }) => {
  if (!isWeb) return <Text style={{ color, fontSize: size }}>{'♪'}</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="11,5 6,9 2,9 2,15 6,15 11,19" />
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
    </svg>
  );
};

const SpeakerOffIcon = ({ color = '#000', size = 18 }) => {
  if (!isWeb) return <Text style={{ color, fontSize: size }}>{'×'}</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="11,5 6,9 2,9 2,15 6,15 11,19" />
      <line x1="23" y1="9" x2="17" y2="15" />
      <line x1="17" y1="9" x2="23" y2="15" />
    </svg>
  );
};

const PanelOpenIcon = ({ color = '#000', size = 18 }) => {
  if (!isWeb) return <Text style={{ color, fontSize: size }}>{'='}</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="15" y1="3" x2="15" y2="21" />
    </svg>
  );
};

const ArrowLeftIcon = ({ color = '#000', size = 16 }) => {
  if (!isWeb) return <Text style={{ color, fontSize: size }}>{'←'}</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12,19 5,12 12,5" />
    </svg>
  );
};

const MicIcon = ({ color = '#000', size = 18 }) => {
  if (!isWeb) return <Text style={{ color, fontSize: size }}>{'🎙'}</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="2" width="6" height="12" rx="3" />
      <path d="M5 10a7 7 0 0 0 14 0" />
      <line x1="12" y1="19" x2="12" y2="22" />
      <line x1="8" y1="22" x2="16" y2="22" />
    </svg>
  );
};

const SparklesIcon = ({ color = '#000', size = 16 }) => {
  if (!isWeb) return <Text style={{ color, fontSize: size }}>{'✦'}</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2 L13.5 8.5 L20 10 L13.5 11.5 L12 18 L10.5 11.5 L4 10 L10.5 8.5 Z" fill={color} stroke="none" />
      <path d="M5 4 L5.7 6.3 L8 7 L5.7 7.7 L5 10 L4.3 7.7 L2 7 L4.3 6.3 Z" fill={color} stroke="none" />
      <path d="M19 14 L19.5 15.8 L21 16.5 L19.5 17.2 L19 19 L18.5 17.2 L17 16.5 L18.5 15.8 Z" fill={color} stroke="none" />
    </svg>
  );
};

const CheckIcon = ({ color = '#000', size = 14 }) => {
  if (!isWeb) return <Text style={{ color, fontSize: size }}>{'✓'}</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20,6 9,17 4,12" />
    </svg>
  );
};

// ─── Staccato Cursor ─────────────────────────────────────────────────────────
const StaccatoCursor = ({ isDark }) => {
  const [position, setPosition] = useState({ x: -100, y: -100 });
  const cursorColor = isDark ? '#E07A98' : '#C75B7A';

  useEffect(() => {
    if (!isWeb) return;
    const handle = (e) => setPosition({ x: e.clientX, y: e.clientY });
    window.addEventListener('mousemove', handle);
    document.body.style.cursor = 'none';
    return () => { window.removeEventListener('mousemove', handle); document.body.style.cursor = 'auto'; };
  }, []);

  if (!isWeb) return null;
  return (
    <div style={{
      position: 'fixed', left: position.x - 4, top: position.y - 4,
      width: 8, height: 8, borderRadius: '50%', backgroundColor: cursorColor,
      pointerEvents: 'none', zIndex: 10000, boxShadow: `0 0 12px ${cursorColor}80`,
    }} />
  );
};

// ─── Count-in Display ────────────────────────────────────────────────────────
const CountInDisplay = ({ count, total, theme }) => {
  const scale = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    Animated.sequence([
      Animated.timing(scale, { toValue: 1.4, duration: 80, useNativeDriver: true }),
      Animated.timing(scale, { toValue: 1, duration: 220, useNativeDriver: true }),
    ]).start();
  }, [count]);

  return (
    <View style={styles.countInOverlay}>
      <View style={[styles.countInBox, { backgroundColor: theme.surface }]}>
        <Animated.Text style={[styles.countInNumber, { color: theme.primary, transform: [{ scale }] }]}>
          {count}
        </Animated.Text>
        <Text style={[styles.countInLabel, { color: theme.textMuted }]}>
          {count === total ? 'Go!' : `of ${total}`}
        </Text>
      </View>
    </View>
  );
};

// ─── Beat Indicator ──────────────────────────────────────────────────────────
const BeatIndicator = ({ currentBeat, beatsPerMeasure, theme }) => (
  <View style={styles.beatIndicator}>
    {[...Array(Math.min(beatsPerMeasure, 8))].map((_, i) => (
      <View
        key={i}
        style={[
          styles.beatDot,
          {
            backgroundColor: i < currentBeat ? theme.primary : theme.border,
            transform: [{ scale: i === currentBeat - 1 ? 1.4 : 1 }],
          },
        ]}
      />
    ))}
  </View>
);

// ─── Stepper Control ─────────────────────────────────────────────────────────
const Stepper = ({ label, value, onDecrement, onIncrement, theme }) => (
  <View style={styles.stepperRow}>
    <Text style={[styles.stepperLabel, { color: theme.textMuted }]}>{label}</Text>
    <View style={styles.stepperControls}>
      <Pressable style={[styles.stepBtn, { backgroundColor: theme.backgroundAlt }]} onPress={onDecrement}>
        <Text style={[styles.stepBtnText, { color: theme.textPrimary }]}>−</Text>
      </Pressable>
      <Text style={[styles.stepValue, { color: theme.textPrimary }]}>{value}</Text>
      <Pressable style={[styles.stepBtn, { backgroundColor: theme.backgroundAlt }]} onPress={onIncrement}>
        <Text style={[styles.stepBtnText, { color: theme.textPrimary }]}>+</Text>
      </Pressable>
    </View>
  </View>
);

// ─── Volume Slider ───────────────────────────────────────────────────────────
const VolumeSlider = ({ value, onChange, theme }) => {
  if (!isWeb) return null;
  return (
    <View style={styles.volumeRow}>
      <View style={styles.volumeIconWrap}>
        {value === 0
          ? <SpeakerOffIcon color={theme.textMuted} size={16} />
          : <SpeakerOnIcon color={theme.primary} size={16} />
        }
      </View>
      <input
        type="range" min="0" max="100"
        value={value * 100}
        onChange={(e) => onChange(e.target.value / 100)}
        style={{
          flex: 1, height: 4, appearance: 'none', cursor: 'pointer',
          background: `linear-gradient(to right, ${theme.primary} ${value * 100}%, ${theme.border} ${value * 100}%)`,
          borderRadius: 2,
        }}
      />
      <Text style={[styles.volumePercent, { color: theme.textMuted }]}>{Math.round(value * 100)}%</Text>
    </View>
  );
};

// ─── Segmented Control ───────────────────────────────────────────────────────
const SegmentedControl = ({ options, value, onChange, theme }) => (
  <View style={[styles.segmented, { backgroundColor: theme.backgroundAlt }]}>
    {options.map((opt) => (
      <Pressable
        key={opt.id}
        style={[
          styles.segmentedOption,
          opt.id === value && [styles.segmentedActive, { backgroundColor: theme.surface, ...Platform.select({ web: { boxShadow: '0 1px 4px rgba(0,0,0,0.12)' } }) }],
        ]}
        onPress={() => onChange(opt.id)}
      >
        <Text style={[
          styles.segmentedText,
          { color: opt.id === value ? theme.textPrimary : theme.textMuted },
          opt.id === value && { fontWeight: '700' },
        ]}>
          {opt.label}
        </Text>
      </Pressable>
    ))}
  </View>
);

// ─── Section Divider ─────────────────────────────────────────────────────────
const SectionLabel = ({ label, theme }) => (
  <Text style={[styles.sectionLabel, { color: theme.textMuted }]}>{label}</Text>
);

// ─── Score Analysis Panel ────────────────────────────────────────────────────
const DIFFICULTY_COLORS = {
  Beginner: '#4caf50',
  Intermediate: '#2196f3',
  Advanced: '#ff9800',
  Professional: '#C75B7A',
};

const ScoreAnalysisPanel = ({ analysis, isAnalyzing, analysisError, onAnalyze, onApplyTempo, theme }) => {
  const [expanded, setExpanded] = useState(false);

  if (isAnalyzing) {
    return (
      <View style={styles.analysisLoadingBox}>
        <SparklesIcon color={theme.primary} size={16} />
        <Text style={[styles.analysisLoadingText, { color: theme.textMuted }]}>Analyzing score…</Text>
      </View>
    );
  }

  if (analysisError) {
    return (
      <View style={styles.analysisErrorBox}>
        <Text style={[styles.analysisErrorText, { color: theme.error || '#e53935' }]}>{analysisError}</Text>
        <Pressable style={[styles.analyzeBtn, { backgroundColor: theme.backgroundAlt }]} onPress={onAnalyze}>
          <Text style={[styles.analyzeBtnText, { color: theme.textPrimary }]}>Retry</Text>
        </Pressable>
      </View>
    );
  }

  if (!analysis) {
    return (
      <Pressable
        style={[styles.analyzeBtn, { backgroundColor: theme.primary }]}
        onPress={onAnalyze}
      >
        <SparklesIcon color="#fff" size={14} />
        <Text style={[styles.analyzeBtnText, { color: '#fff' }]}>Analyze with AI</Text>
      </Pressable>
    );
  }

  const diffColor = DIFFICULTY_COLORS[analysis.difficulty] || theme.primary;

  return (
    <View style={styles.analysisResults}>
      {/* Badges row */}
      <View style={styles.analysisBadgeRow}>
        {analysis.keySignature && analysis.keySignature !== 'Unknown' && (
          <View style={[styles.analysisBadge, { backgroundColor: theme.backgroundAlt }]}>
            <Text style={[styles.analysisBadgeText, { color: theme.textPrimary }]}>♩ {analysis.keySignature}</Text>
          </View>
        )}
        {analysis.timeSignature && (
          <View style={[styles.analysisBadge, { backgroundColor: theme.backgroundAlt }]}>
            <Text style={[styles.analysisBadgeText, { color: theme.textPrimary }]}>{analysis.timeSignature}</Text>
          </View>
        )}
        {analysis.difficulty && (
          <View style={[styles.analysisBadge, { backgroundColor: diffColor + '22' }]}>
            <Text style={[styles.analysisBadgeText, { color: diffColor }]}>{analysis.difficulty}</Text>
          </View>
        )}
      </View>

      {/* Tempo row */}
      {(analysis.tempoMarking || analysis.suggestedBpm) && (
        <View style={styles.analysisTempoRow}>
          <View style={styles.analysisTempoInfo}>
            {analysis.tempoMarking && (
              <Text style={[styles.analysisTempoLabel, { color: theme.textPrimary }]}>{analysis.tempoMarking}</Text>
            )}
            {analysis.suggestedBpm && (
              <Text style={[styles.analysisBpmText, { color: theme.textMuted }]}>
                {analysis.bpmRange ? `♩ = ${analysis.bpmRange}` : `♩ = ${analysis.suggestedBpm}`}
              </Text>
            )}
          </View>
          <Pressable
            style={[styles.applyTempoBtn, { backgroundColor: theme.primary + '18', borderColor: theme.primary }]}
            onPress={onApplyTempo}
          >
            <CheckIcon color={theme.primary} size={12} />
            <Text style={[styles.applyTempoBtnText, { color: theme.primary }]}>Apply</Text>
          </Pressable>
        </View>
      )}

      {/* Composer / Title */}
      {(analysis.composer || analysis.title) && (
        <Text style={[styles.analysisComposerText, { color: theme.textMuted }]}>
          {[analysis.title, analysis.composer].filter(Boolean).join(' — ')}
        </Text>
      )}

      {/* Style + Instrument */}
      {(analysis.style || analysis.instrument) && (
        <Text style={[styles.analysisMetaText, { color: theme.textMuted }]}>
          {[analysis.instrument, analysis.style].filter(Boolean).join(' · ')}
        </Text>
      )}

      {/* Summary toggle */}
      {analysis.summary && (
        <Pressable onPress={() => setExpanded(e => !e)} style={styles.analysisSummaryToggle}>
          <Text style={[styles.analysisSummaryText, { color: theme.textMuted }]} numberOfLines={expanded ? undefined : 2}>
            {analysis.summary}
          </Text>
          <Text style={[styles.analysisShowMore, { color: theme.primary }]}>
            {expanded ? 'Show less' : 'Show more'}
          </Text>
        </Pressable>
      )}

      {/* Notable features */}
      {expanded && analysis.notableFeatures?.length > 0 && (
        <View style={styles.analysisFeaturesWrap}>
          {analysis.notableFeatures.map((f, i) => (
            <View key={i} style={[styles.analysisFeatureChip, { backgroundColor: theme.backgroundAlt }]}>
              <Text style={[styles.analysisFeatureText, { color: theme.textMuted }]}>{f}</Text>
            </View>
          ))}
        </View>
      )}

      {/* Re-analyze */}
      <Pressable onPress={onAnalyze} style={styles.reAnalyzeBtn}>
        <Text style={[styles.reAnalyzeBtnText, { color: theme.textMuted }]}>Re-analyze</Text>
      </Pressable>
    </View>
  );
};

// ─── Page Turn Controls Panel ────────────────────────────────────────────────
const PageTurnControls = ({
  mode, setMode,
  bpm, setBpm,
  beatsPerMeasure, setBeatsPerMeasure,
  measuresPerPage, setMeasuresPerPage,
  countInBars, setCountInBars,
  isPlaying, onPlay, onStop,
  theme, currentPage, totalPages,
  onPrevPage, onNextPage,
  viewMode, setViewMode,
  metronomeSound, setMetronomeSound,
  metronomeVolume, setMetronomeVolume,
  isMobile,
  // AI analysis
  aiAnalysis, isAnalyzing, analysisError, onAnalyze, onApplyTempo,
}) => {
  const pageModes = [
    { id: 'manual', label: 'Manual' },
    { id: 'timer', label: 'Auto BPM' },
  ];
  const viewModes = [
    { id: 'single', label: '1 Page' },
    { id: 'sideBySide', label: '2 Pages' },
  ];
  const prevDisabled = currentPage <= 1;
  const nextDisabled = currentPage >= totalPages;

  return (
    <View style={[styles.panelContent, { backgroundColor: theme.surface }]}>

      {/* Navigation */}
      <View style={styles.navSection}>
        <Pressable
          style={[styles.navPageBtn, { backgroundColor: theme.backgroundAlt, opacity: prevDisabled ? 0.35 : 1 }]}
          onPress={onPrevPage}
          disabled={prevDisabled}
        >
          <ChevronLeftIcon color={theme.textPrimary} size={18} />
          <Text style={[styles.navPageBtnText, { color: theme.textPrimary }]}>Prev</Text>
        </Pressable>

        <View style={[styles.pageCounter, { backgroundColor: theme.backgroundAlt }]}>
          <Text style={[styles.pageCounterValue, { color: theme.textPrimary }]}>{currentPage}</Text>
          <Text style={[styles.pageCounterTotal, { color: theme.textMuted }]}>/ {totalPages}</Text>
        </View>

        <Pressable
          style={[styles.navPageBtn, { backgroundColor: theme.backgroundAlt, opacity: nextDisabled ? 0.35 : 1 }]}
          onPress={onNextPage}
          disabled={nextDisabled}
        >
          <Text style={[styles.navPageBtnText, { color: theme.textPrimary }]}>Next</Text>
          <ChevronRightIcon color={theme.textPrimary} size={18} />
        </Pressable>
      </View>

      <View style={[styles.divider, { backgroundColor: theme.border }]} />

      {/* Mode */}
      <SectionLabel label="PAGE TURN" theme={theme} />
      <SegmentedControl options={pageModes} value={mode} onChange={setMode} theme={theme} />

      {/* BPM settings — only in auto mode */}
      {mode === 'timer' && (
        <>
          <View style={[styles.divider, { backgroundColor: theme.border }]} />
          <SectionLabel label="TEMPO & TIMING" theme={theme} />

          <Stepper
            label="BPM"
            value={bpm}
            onDecrement={() => setBpm(Math.max(40, bpm - 5))}
            onIncrement={() => setBpm(Math.min(240, bpm + 5))}
            theme={theme}
          />
          <Stepper
            label="Time Signature"
            value={`${beatsPerMeasure}/4`}
            onDecrement={() => setBeatsPerMeasure(Math.max(2, beatsPerMeasure - 1))}
            onIncrement={() => setBeatsPerMeasure(Math.min(12, beatsPerMeasure + 1))}
            theme={theme}
          />
          <Stepper
            label="Measures / Page"
            value={measuresPerPage}
            onDecrement={() => setMeasuresPerPage(Math.max(1, measuresPerPage - 1))}
            onIncrement={() => setMeasuresPerPage(Math.min(16, measuresPerPage + 1))}
            theme={theme}
          />
          <Stepper
            label="Count-in Bars"
            value={`${countInBars} ${countInBars === 1 ? 'bar' : 'bars'}`}
            onDecrement={() => setCountInBars(Math.max(0, countInBars - 1))}
            onIncrement={() => setCountInBars(Math.min(4, countInBars + 1))}
            theme={theme}
          />

          <View style={[styles.divider, { backgroundColor: theme.border }]} />
          <SectionLabel label="METRONOME" theme={theme} />

          {/* Sound on/off */}
          <Pressable
            style={[styles.soundToggleBtn, {
              backgroundColor: metronomeSound ? theme.primary + '18' : theme.backgroundAlt,
              borderColor: metronomeSound ? theme.primary : theme.border,
            }]}
            onPress={() => setMetronomeSound(!metronomeSound)}
          >
            <View style={styles.soundToggleLeft}>
              {metronomeSound
                ? <SpeakerOnIcon color={theme.primary} size={16} />
                : <SpeakerOffIcon color={theme.textMuted} size={16} />
              }
              <Text style={[styles.soundToggleText, { color: metronomeSound ? theme.primary : theme.textMuted }]}>
                {metronomeSound ? 'Sound On' : 'Sound Off'}
              </Text>
            </View>
            <View style={[styles.soundToggleDot, {
              backgroundColor: metronomeSound ? theme.primary : theme.border,
            }]} />
          </Pressable>

          {metronomeSound && (
            <VolumeSlider value={metronomeVolume} onChange={setMetronomeVolume} theme={theme} />
          )}
        </>
      )}

      {mode === 'manual' && (
        <View style={styles.manualTip}>
          <Text style={[styles.manualTipText, { color: theme.textMuted }]}>
            Tap the left or right side of the sheet, or use arrow keys / spacebar
          </Text>
        </View>
      )}

      {/* View mode */}
      {!isMobile && (
        <>
          <View style={[styles.divider, { backgroundColor: theme.border }]} />
          <SectionLabel label="LAYOUT" theme={theme} />
          <SegmentedControl options={viewModes} value={viewMode} onChange={setViewMode} theme={theme} />
        </>
      )}

      {/* Play / Stop */}
      {mode === 'timer' && (
        <>
          <View style={[styles.divider, { backgroundColor: theme.border }]} />
          <Pressable
            style={[styles.playStopBtn, { backgroundColor: isPlaying ? theme.error : theme.primary }]}
            onPress={isPlaying ? onStop : onPlay}
          >
            <View style={styles.playStopBtnInner}>
              {isPlaying
                ? <StopIcon color="#fff" size={18} />
                : <PlayIcon color="#fff" size={18} />
              }
              <Text style={styles.playStopBtnText}>
                {isPlaying ? 'Stop' : 'Play'}
              </Text>
            </View>
          </Pressable>
        </>
      )}

      {/* AI Score Analysis */}
      <View style={[styles.divider, { backgroundColor: theme.border }]} />
      <View style={styles.analysisSectionHeader}>
        <SparklesIcon color={theme.primary} size={13} />
        <Text style={[styles.sectionLabel, { color: theme.textMuted, marginBottom: 0, marginTop: 0 }]}>SCORE ANALYSIS</Text>
      </View>
      <ScoreAnalysisPanel
        analysis={aiAnalysis}
        isAnalyzing={isAnalyzing}
        analysisError={analysisError}
        onAnalyze={onAnalyze}
        onApplyTempo={onApplyTempo}
        theme={theme}
      />
    </View>
  );
};

// ─── Sheet Music Display ──────────────────────────────────────────────────────
const SheetMusicDisplay = ({ sheet, currentPage, theme }) => {
  const [imageError, setImageError] = useState(false);

  const isImage = sheet?.fileType?.startsWith('image/') ||
    sheet?.fileName?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
  const isPDF = sheet?.fileType === 'application/pdf' ||
    sheet?.fileName?.match(/\.pdf$/i);

  if (!sheet?.fileUrl) {
    return (
      <View style={[styles.sheetPage, { justifyContent: 'center', alignItems: 'center', backgroundColor: theme.surface }]}>
        <Text style={{ color: theme.textMuted, fontSize: 16 }}>No file available</Text>
      </View>
    );
  }

  return (
    <View style={styles.sheetPage}>
      {isImage && !imageError ? (
        isWeb ? (
          <img
            src={sheet.fileUrl} alt={sheet.title}
            style={{ width: '100%', height: '100%', objectFit: 'contain', backgroundColor: '#fff' }}
            onError={() => setImageError(true)}
          />
        ) : null
      ) : isPDF ? (
        isWeb ? (
          <iframe
            src={`${sheet.fileUrl}#page=${currentPage}&toolbar=0&navpanes=0&scrollbar=0`}
            style={{ width: '100%', height: '100%', border: 'none', backgroundColor: '#fff', display: 'block' }}
            title={sheet.title}
          />
        ) : (
          <View style={styles.fallbackContainer}>
            <Text style={[styles.fallbackText, { color: theme.textMuted }]}>PDF — Page {currentPage}</Text>
          </View>
        )
      ) : (
        <View style={styles.fallbackContainer}>
          <Text style={[styles.fallbackText, { color: theme.textMuted }]}>Cannot preview this file</Text>
        </View>
      )}
    </View>
  );
};

// ─── Main Screen ─────────────────────────────────────────────────────────────
export const ViewerScreen = ({ navigation, route }) => {
  const { sheetId, sheetData } = route?.params || {};
  const { user } = useAuth();
  const { sheets, updateSheet } = useSheets();

  const [isDark, setIsDark] = useState(getDarkMode());
  const theme = isDark ? baseTheme.dark : baseTheme.light;

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(3);
  const [sheet, setSheet] = useState(sheetData || null);

  const [pageTurnMode, setPageTurnMode] = useState('manual');
  const [isPlaying, setIsPlaying] = useState(false);
  const [bpm, setBpm] = useState(120);
  const [beatsPerMeasure, setBeatsPerMeasure] = useState(4);
  const [measuresPerPage, setMeasuresPerPage] = useState(8);
  const [countInBars, setCountInBars] = useState(1);

  const [isCountingIn, setIsCountingIn] = useState(false);
  const [countInBeat, setCountInBeat] = useState(0);
  const [currentBeat, setCurrentBeat] = useState(0);

  const [viewMode, setViewMode] = useState('single');
  const [metronomeSound, setMetronomeSound] = useState(true);
  const [metronomeVolume, setMetronomeVolume] = useState(0.7);

  const [showControls, setShowControls] = useState(true);
  const [showHeader, setShowHeader] = useState(true);
  const headerOpacity = useRef(new Animated.Value(1)).current;
  const mouseIdleTimerRef = useRef(null);

  const beatIntervalRef = useRef(null);
  const countInIntervalRef = useRef(null);

  // Voice commands
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const voiceSupported = isVoiceSupported();

  // AI analysis
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);
  // aiAnalysis lives on the sheet doc — read from sheet.aiAnalysis

  const isMobile = SCREEN_WIDTH < 768;
  const PANEL_WIDTH = 300;

  // Load sheet data
  useEffect(() => {
    if (!sheet && sheetId && sheets.length > 0) {
      const found = sheets.find(s => s.id === sheetId);
      if (found) {
        setSheet(found);
        setTotalPages(found.pageCount || 3);
        if (found.pageTurnSettings) {
          setPageTurnMode(found.pageTurnSettings.mode || 'manual');
          setBpm(found.pageTurnSettings.bpm || 120);
          setMeasuresPerPage(found.pageTurnSettings.measuresPerPage || 8);
        }
      }
    }
  }, [sheetId, sheets, sheet]);

  // Sync dark mode
  useEffect(() => {
    if (!isWeb) return;
    const handle = (e) => { if (e.key === 'sheetshed_darkmode') setIsDark(e.newValue === 'true'); };
    window.addEventListener('storage', handle);
    return () => window.removeEventListener('storage', handle);
  }, []);

  // Page title
  useEffect(() => {
    if (isWeb) document.title = `${sheet?.title || 'Viewer'} - SheetShed`;
  }, [sheet]);

  // Mouse idle → fade header during playback
  useEffect(() => {
    if (!isWeb) return;
    const resetIdle = () => {
      setShowHeader(true);
      Animated.timing(headerOpacity, { toValue: 1, duration: 150, useNativeDriver: true }).start();
      clearTimeout(mouseIdleTimerRef.current);
      if (isPlaying || isCountingIn) {
        mouseIdleTimerRef.current = setTimeout(() => {
          Animated.timing(headerOpacity, { toValue: 0, duration: 600, useNativeDriver: true })
            .start(() => setShowHeader(false));
        }, 3000);
      }
    };
    window.addEventListener('mousemove', resetIdle);
    resetIdle();
    return () => { window.removeEventListener('mousemove', resetIdle); clearTimeout(mouseIdleTimerRef.current); };
  }, [isPlaying, isCountingIn]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!isWeb) return;
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); handlePageTurn('next'); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); handlePageTurn('prev'); }
      else if (e.key === 'Escape') { stopPlayback(); navigation.goBack(); }
      else if (e.key === 'p' || e.key === 'Enter') {
        e.preventDefault();
        if (isPlaying || isCountingIn) stopPlayback(); else startPlayback();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPage, totalPages, isPlaying, isCountingIn]);

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      if (beatIntervalRef.current) clearInterval(beatIntervalRef.current);
      if (countInIntervalRef.current) clearInterval(countInIntervalRef.current);
    };
  }, []);

  const beatDurationMs = (60 * 1000) / bpm;

  const initAudio = () => {
    if (isWeb) { const ctx = getAudioContext(); if (ctx?.state === 'suspended') ctx.resume(); }
  };

  const startPlayback = () => {
    initAudio();
    if (countInBars > 0) {
      setIsCountingIn(true);
      setCountInBeat(1);
      if (metronomeSound) playMetronomeClick(metronomeVolume, true);
      let beat = 1;
      const total = countInBars * beatsPerMeasure;
      countInIntervalRef.current = setInterval(() => {
        beat++;
        if (beat > total) {
          clearInterval(countInIntervalRef.current);
          setIsCountingIn(false); setCountInBeat(0);
          beginPlayback();
        } else {
          setCountInBeat(beat);
          if (metronomeSound) playMetronomeClick(metronomeVolume, (beat - 1) % beatsPerMeasure === 0);
        }
      }, beatDurationMs);
    } else {
      beginPlayback();
    }
  };

  const beginPlayback = () => {
    setIsPlaying(true); setCurrentBeat(1);
    if (metronomeSound) playMetronomeClick(metronomeVolume, true);
    let beat = 1, measureBeat = 1;
    const beatsPerPage = measuresPerPage * beatsPerMeasure;
    const pagesPerTurn = viewMode === 'sideBySide' ? 2 : 1;
    beatIntervalRef.current = setInterval(() => {
      beat++;
      measureBeat = ((beat - 1) % beatsPerMeasure) + 1;
      setCurrentBeat(measureBeat);
      if (metronomeSound) playMetronomeClick(metronomeVolume, measureBeat === 1);
      if (beat >= beatsPerPage) {
        beat = 0;
        setCurrentPage(prev => {
          const next = prev + pagesPerTurn;
          if (next > totalPages) { stopPlayback(); return prev; }
          return next;
        });
      }
    }, beatDurationMs);
  };

  const stopPlayback = () => {
    setIsPlaying(false); setIsCountingIn(false); setCountInBeat(0); setCurrentBeat(0);
    if (beatIntervalRef.current) { clearInterval(beatIntervalRef.current); beatIntervalRef.current = null; }
    if (countInIntervalRef.current) { clearInterval(countInIntervalRef.current); countInIntervalRef.current = null; }
  };

  const handlePageTurn = (direction) => {
    const step = viewMode === 'sideBySide' && !isMobile ? 2 : 1;
    if (direction === 'next' && currentPage < totalPages) setCurrentPage(p => Math.min(p + step, totalPages));
    else if (direction === 'prev' && currentPage > 1) setCurrentPage(p => Math.max(p - step, 1));
  };

  // Voice commands — wire turn/play/stop
  useVoiceCommands({
    enabled: voiceEnabled,
    onNextPage: useCallback(() => handlePageTurn('next'), [currentPage, totalPages, viewMode, isMobile]),
    onPrevPage: useCallback(() => handlePageTurn('prev'), [currentPage, viewMode, isMobile]),
    onPlay: useCallback(() => { if (!isPlaying && !isCountingIn) startPlayback(); }, [isPlaying, isCountingIn]),
    onStop: useCallback(() => { if (isPlaying || isCountingIn) stopPlayback(); }, [isPlaying, isCountingIn]),
  });

  // AI score analysis
  const handleAnalyzeScore = async () => {
    if (!sheet?.fileUrl || !sheet?.id) return;
    setIsAnalyzing(true);
    setAnalysisError(null);
    try {
      const analyzeScore = httpsCallable(functions, 'analyzeScore');
      await analyzeScore({ sheetId: sheet.id, fileUrl: sheet.fileUrl, fileType: sheet.fileType });
      // Firestore listener in useSheets will push the updated sheet (with aiAnalysis) back down automatically
    } catch (e) {
      setAnalysisError(e?.message || 'Analysis failed. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Apply AI tempo suggestions to page-turn settings
  const handleApplyAiTempo = () => {
    const a = sheet?.aiAnalysis;
    if (!a) return;
    if (a.suggestedBpm) setBpm(Math.min(240, Math.max(40, Math.round(a.suggestedBpm))));
    if (a.beatsPerMeasure) setBeatsPerMeasure(Math.min(12, Math.max(2, a.beatsPerMeasure)));
    if (a.estimatedMeasuresPerPage) setMeasuresPerPage(Math.min(16, Math.max(1, a.estimatedMeasuresPerPage)));
    setPageTurnMode('timer');
  };

  const saveSettings = async () => {
    if (sheet && user) {
      try {
        await updateSheet(sheet.id, {
          pageTurnSettings: { mode: pageTurnMode, bpm, beatsPerMeasure, measuresPerPage, countInBars },
        });
      } catch (e) { /* best-effort */ }
    }
  };

  const handleBack = async () => {
    stopPlayback();
    await saveSettings();
    navigation.goBack();
  };

  return (
    <View style={[styles.container, { backgroundColor: '#000' }]}>
      <StaccatoCursor isDark={isDark} />

      {/* Count-in overlay */}
      {isCountingIn && (
        <CountInDisplay count={Math.ceil(countInBeat / beatsPerMeasure) || 1} total={countInBars} theme={theme} />
      )}

      {/* Sheet music fills everything */}
      <View style={styles.sheetContainer}>
        {viewMode === 'sideBySide' && !isMobile ? (
          <View style={styles.sideBySideContainer}>
            <SheetMusicDisplay sheet={sheet} currentPage={currentPage} theme={theme} />
            {currentPage + 1 <= totalPages && (
              <SheetMusicDisplay sheet={sheet} currentPage={currentPage + 1} theme={theme} />
            )}
          </View>
        ) : (
          <SheetMusicDisplay sheet={sheet} currentPage={currentPage} theme={theme} />
        )}
      </View>

      {/* Tap zones */}
      <Pressable style={[styles.tapZone, styles.tapZoneLeft]} onPress={() => handlePageTurn('prev')} />
      <Pressable style={[styles.tapZone, styles.tapZoneRight]} onPress={() => handlePageTurn('next')} />

      {/* Floating header */}
      <Animated.View
        style={[styles.header, { backgroundColor: theme.surfaceFrosted, borderBottomColor: theme.border, opacity: headerOpacity }]}
        pointerEvents={showHeader ? 'auto' : 'none'}
      >
        {/* Back */}
        <Pressable style={styles.headerBtn} onPress={handleBack}>
          <ArrowLeftIcon color={theme.textSecondary} size={18} />
          <Text style={[styles.headerBtnText, { color: theme.textSecondary }]}>Library</Text>
        </Pressable>

        {/* Title + page */}
        <View style={styles.headerCenter}>
          <Text style={[styles.headerTitle, { color: theme.textPrimary }]} numberOfLines={1}>
            {sheet?.title || 'Untitled'}
          </Text>
          <View style={styles.headerMeta}>
            <Text style={[styles.headerPage, { color: theme.textMuted }]}>
              Page {currentPage} of {totalPages}
            </Text>
            {isPlaying && (
              <BeatIndicator currentBeat={currentBeat} beatsPerMeasure={beatsPerMeasure} theme={theme} />
            )}
          </View>
        </View>

        {/* Voice command toggle */}
        {voiceSupported && (
          <Pressable
            style={[
              styles.headerIconBtn,
              {
                backgroundColor: voiceEnabled ? '#e53935' + '22' : theme.backgroundAlt,
                borderColor: voiceEnabled ? '#e53935' : 'transparent',
                marginRight: 8,
              },
            ]}
            onPress={() => setVoiceEnabled(v => !v)}
          >
            <MicIcon color={voiceEnabled ? '#e53935' : theme.textSecondary} size={18} />
            {voiceEnabled && (
              <View style={styles.micPulseDot} />
            )}
          </Pressable>
        )}

        {/* Controls toggle in header */}
        <Pressable
          style={[styles.headerIconBtn, { backgroundColor: showControls ? theme.primary + '18' : theme.backgroundAlt, borderColor: showControls ? theme.primary : 'transparent' }]}
          onPress={() => setShowControls(!showControls)}
        >
          <PanelOpenIcon color={showControls ? theme.primary : theme.textSecondary} size={18} />
        </Pressable>
      </Animated.View>

      {/* Right-side controls panel — flex child, not absolutely positioned */}
      {showControls && (
        <View
          style={[
            styles.controlsPanel,
            {
              backgroundColor: theme.surface,
              borderLeftColor: theme.border,
              width: isMobile ? '100%' : PANEL_WIDTH,
              // On mobile, push to bottom
              ...(isMobile ? { position: 'absolute', bottom: 0, left: 0, right: 0, top: 'auto', maxHeight: '70%' } : {}),
            },
          ]}
        >
          <ScrollView
            showsVerticalScrollIndicator={false}
            contentContainerStyle={[styles.controlsPanelInner, { paddingTop: isMobile ? 16 : 72 }]}
          >
            <PageTurnControls
              mode={pageTurnMode} setMode={setPageTurnMode}
              bpm={bpm} setBpm={setBpm}
              beatsPerMeasure={beatsPerMeasure} setBeatsPerMeasure={setBeatsPerMeasure}
              measuresPerPage={measuresPerPage} setMeasuresPerPage={setMeasuresPerPage}
              countInBars={countInBars} setCountInBars={setCountInBars}
              isPlaying={isPlaying || isCountingIn}
              onPlay={startPlayback} onStop={stopPlayback}
              theme={theme}
              currentPage={currentPage} totalPages={totalPages}
              onPrevPage={() => handlePageTurn('prev')}
              onNextPage={() => handlePageTurn('next')}
              viewMode={viewMode} setViewMode={setViewMode}
              metronomeSound={metronomeSound} setMetronomeSound={setMetronomeSound}
              metronomeVolume={metronomeVolume} setMetronomeVolume={setMetronomeVolume}
              isMobile={isMobile}
              aiAnalysis={sheet?.aiAnalysis || null}
              isAnalyzing={isAnalyzing}
              analysisError={analysisError}
              onAnalyze={handleAnalyzeScore}
              onApplyTempo={handleApplyAiTempo}
            />
          </ScrollView>
        </View>
      )}
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
  },

  // Sheet fills all space left of the panel
  sheetContainer: { flex: 1 },
  sheetPage: { flex: 1, backgroundColor: '#fff', overflow: 'hidden' },
  fallbackContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 40 },
  fallbackText: { fontSize: 18, fontWeight: '600' },
  sideBySideContainer: { flex: 1, flexDirection: 'row' },

  // Tap zones
  tapZone: { position: 'absolute', top: 0, bottom: 0, width: '12%', zIndex: 20 },
  tapZoneLeft: { left: 0 },
  tapZoneRight: { right: 0 },

  // Header
  header: {
    position: 'absolute', top: 0, left: 0, right: 0, zIndex: 100,
    flexDirection: 'row', alignItems: 'center',
    paddingHorizontal: 16, paddingVertical: 10,
    borderBottomWidth: 1,
    ...Platform.select({ web: { backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' } }),
  },
  headerBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 6, paddingRight: 12 },
  headerBtnText: { fontSize: 14, fontWeight: '500' },
  headerCenter: { flex: 1, alignItems: 'center' },
  headerTitle: { fontSize: 16, fontWeight: '700' },
  headerMeta: { flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 2 },
  headerPage: { fontSize: 12 },
  headerIconBtn: {
    width: 36, height: 36, borderRadius: 10,
    justifyContent: 'center', alignItems: 'center',
    borderWidth: 1,
  },

  // Beat
  beatIndicator: { flexDirection: 'row', gap: 3 },
  beatDot: { width: 7, height: 7, borderRadius: 4 },

  // Count-in
  countInOverlay: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    justifyContent: 'center', alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.65)', zIndex: 300,
  },
  countInBox: {
    padding: 48, borderRadius: 28, alignItems: 'center',
    ...Platform.select({ web: { boxShadow: '0 12px 48px rgba(0,0,0,0.4)' } }),
  },
  countInNumber: { fontSize: 88, fontWeight: '800', lineHeight: 96 },
  countInLabel: { fontSize: 17, marginTop: 4 },

  // Right panel
  controlsPanel: {
    borderLeftWidth: 1,
    zIndex: 50,
    ...Platform.select({
      web: { boxShadow: '-2px 0 24px rgba(0,0,0,0.12)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' },
    }),
  },
  controlsPanelInner: { paddingBottom: 32 },

  // Panel content
  panelContent: { paddingHorizontal: 20, paddingVertical: 8 },

  // Section labels
  sectionLabel: {
    fontSize: 10, fontWeight: '700', letterSpacing: 1,
    textTransform: 'uppercase', marginBottom: 10, marginTop: 4,
  },

  divider: { height: 1, marginVertical: 16 },

  // Navigation row
  navSection: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 4 },
  navPageBtn: {
    flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 4, paddingVertical: 10, borderRadius: 10,
  },
  navPageBtnText: { fontSize: 13, fontWeight: '600' },
  pageCounter: {
    paddingHorizontal: 14, paddingVertical: 10, borderRadius: 10,
    flexDirection: 'row', alignItems: 'baseline', gap: 3,
  },
  pageCounterValue: { fontSize: 18, fontWeight: '800' },
  pageCounterTotal: { fontSize: 13 },

  // Segmented control
  segmented: { flexDirection: 'row', borderRadius: 10, padding: 3, marginBottom: 4 },
  segmentedOption: { flex: 1, paddingVertical: 8, alignItems: 'center', borderRadius: 8 },
  segmentedActive: { borderRadius: 8 },
  segmentedText: { fontSize: 13 },

  // Stepper
  stepperRow: {
    flexDirection: 'row', alignItems: 'center',
    justifyContent: 'space-between', marginBottom: 10,
  },
  stepperLabel: { fontSize: 13, flex: 1 },
  stepperControls: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  stepBtn: {
    width: 30, height: 30, borderRadius: 8,
    justifyContent: 'center', alignItems: 'center',
  },
  stepBtnText: { fontSize: 18, fontWeight: '500', lineHeight: 20 },
  stepValue: { fontSize: 14, fontWeight: '700', minWidth: 52, textAlign: 'center' },

  // Sound toggle
  soundToggleBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 14, paddingVertical: 11,
    borderRadius: 10, borderWidth: 1, marginBottom: 12,
  },
  soundToggleLeft: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  soundToggleText: { fontSize: 13, fontWeight: '600' },
  soundToggleDot: { width: 8, height: 8, borderRadius: 4 },

  // Volume
  volumeRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 4 },
  volumeIconWrap: { width: 20, alignItems: 'center' },
  volumePercent: { fontSize: 11, minWidth: 30, textAlign: 'right' },

  // Manual tip
  manualTip: { paddingVertical: 12 },
  manualTipText: { fontSize: 12, lineHeight: 18, textAlign: 'center' },

  // Play/Stop
  playStopBtn: {
    borderRadius: 12, paddingVertical: 14,
    alignItems: 'center', marginTop: 4,
    ...Platform.select({ web: { boxShadow: '0 4px 16px rgba(0,0,0,0.2)' } }),
  },
  playStopBtnInner: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  playStopBtnText: { color: '#fff', fontSize: 16, fontWeight: '700' },

  // Mic pulse indicator
  micPulseDot: {
    position: 'absolute', top: 4, right: 4,
    width: 7, height: 7, borderRadius: 4,
    backgroundColor: '#e53935',
    ...Platform.select({ web: { boxShadow: '0 0 0 2px rgba(229,57,53,0.3)' } }),
  },

  // AI analysis section header
  analysisSectionHeader: {
    flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 12, marginTop: 4,
  },

  // Analyze button (pre-analysis)
  analyzeBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 8, paddingVertical: 12, borderRadius: 12, marginBottom: 4,
    ...Platform.select({ web: { boxShadow: '0 4px 16px rgba(0,0,0,0.18)' } }),
  },
  analyzeBtnText: { fontSize: 14, fontWeight: '700' },

  // Loading
  analysisLoadingBox: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 8, paddingVertical: 16,
  },
  analysisLoadingText: { fontSize: 13 },

  // Error
  analysisErrorBox: { gap: 10 },
  analysisErrorText: { fontSize: 12, lineHeight: 17 },

  // Results
  analysisResults: { gap: 10 },
  analysisBadgeRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  analysisBadge: {
    paddingHorizontal: 10, paddingVertical: 5, borderRadius: 20,
  },
  analysisBadgeText: { fontSize: 11, fontWeight: '700' },

  analysisTempoRow: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
  },
  analysisTempoInfo: { gap: 2 },
  analysisTempoLabel: { fontSize: 13, fontWeight: '700' },
  analysisBpmText: { fontSize: 12 },

  applyTempoBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 5,
    paddingHorizontal: 12, paddingVertical: 7, borderRadius: 8, borderWidth: 1,
  },
  applyTempoBtnText: { fontSize: 12, fontWeight: '700' },

  analysisComposerText: { fontSize: 12, fontStyle: 'italic' },
  analysisMetaText: { fontSize: 11 },

  analysisSummaryToggle: { gap: 4 },
  analysisSummaryText: { fontSize: 12, lineHeight: 17 },
  analysisShowMore: { fontSize: 11, fontWeight: '600' },

  analysisFeaturesWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 2 },
  analysisFeatureChip: { paddingHorizontal: 9, paddingVertical: 4, borderRadius: 16 },
  analysisFeatureText: { fontSize: 11 },

  reAnalyzeBtn: { alignSelf: 'flex-start', marginTop: 4 },
  reAnalyzeBtnText: { fontSize: 11, textDecorationLine: 'underline' },
});

export default ViewerScreen;
